import { useEffect, useRef } from "react";
import * as THREE from "three";

type UploadFluidBackgroundProps = {
  className?: string;
  color1?: string;
  color2?: string;
  color3?: string;
  color4?: string;
};

const vertexShader = `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fluidShader = `
  precision highp float;

  uniform float iTime;
  uniform vec2 iResolution;
  uniform vec4 iMouse;
  uniform int iFrame;
  uniform sampler2D iPreviousFrame;
  uniform float uBrushSize;
  uniform float uBrushStrength;
  uniform float uFluidDecay;
  uniform float uTrailLength;
  uniform float uStopDecay;
  uniform float uFlowSpeed;

  varying vec2 vUv;
  vec2 ur, U;

  float ln(vec2 p, vec2 a, vec2 b) {
    return length(p - a - (b - a) * clamp(dot(p - a, b - a) / dot(b - a, b - a), 0.0, 1.0));
  }

  vec4 t(vec2 v, int a, int b) {
    return texture2D(iPreviousFrame, fract((v + vec2(float(a), float(b))) / ur));
  }

  vec4 t(vec2 v) {
    return texture2D(iPreviousFrame, fract(v / ur));
  }

  float area(vec2 a, vec2 b, vec2 c) {
    float A = length(b - c), B = length(c - a), C = length(a - b), s = 0.5 * (A + B + C);
    return sqrt(max(0.0, s * (s - A) * (s - B) * (s - C)));
  }

  void main() {
    U = vUv * iResolution;
    ur = iResolution.xy;

    if (iFrame < 1) {
      float w = 0.5 + sin(0.2 * U.x) * 0.5;
      float q = length(U - 0.5 * ur);
      gl_FragColor = vec4(0.1 * exp(-0.001 * q * q), 0.0, 0.0, w);
    } else {
      vec2 v = U, A = v + vec2(1,1), B = v + vec2(1,-1), C = v + vec2(-1,1), D = v + vec2(-1,-1);

      for (int i = 0; i < 8; i++) {
          v -= uFlowSpeed * t(v).xy;
          A -= uFlowSpeed * t(A).xy;
          B -= uFlowSpeed * t(B).xy;
          C -= uFlowSpeed * t(C).xy;
          D -= uFlowSpeed * t(D).xy;
      }

      vec4 me = t(v);
      vec4 n = t(v, 0, 1), e = t(v, 1, 0), s = t(v, 0, -1), wv = t(v, -1, 0);
      vec4 ne = 0.25 * (n + e + s + wv);
      me = mix(t(v), ne, vec4(0.15, 0.15, 0.95, 0.0));
      me.z -= uFlowSpeed * 0.01 * ((area(A, B, C) + area(B, C, D)) - 4.0);

      vec4 pr = vec4(e.z, wv.z, n.z, s.z);
      me.xy = me.xy + (100.0 * uFlowSpeed) * vec2(pr.x - pr.y, pr.z - pr.w) / ur;

      float decay = pow(uFluidDecay, uFlowSpeed);
      me.xy *= decay;
      me.z  *= uTrailLength;

      if (iMouse.z > 0.0) {
        vec2 mousePos  = iMouse.xy;
        vec2 mousePrev = iMouse.zw;
        float q = ln(U, mousePos, mousePrev);
        vec2 m = mousePos - mousePrev;
        float l = length(m);
        if (l > 0.0) m = min(l, 10.0) * m / l;

        float brushSizeFactor = 1e-4 / uBrushSize;
        float strengthFactor  = 0.03 * uBrushStrength;
        float falloff = pow(exp(-brushSizeFactor * q * q * q), 0.5);

        me.xyw += (strengthFactor * uFlowSpeed) * falloff * vec3(m, 10.0);

        if (length(mousePos - mousePrev) < 2.0) {
          float influence = exp(-length(U - mousePos) * 0.01);
          float cursorDecay = mix(1.0, uStopDecay, influence);
          me.xy *= cursorDecay;
          me.z  *= cursorDecay;
        }
      }
      gl_FragColor = clamp(me, -0.4, 0.4);
    }
  }
`;

const displayShader = `
  precision highp float;
  precision highp int;

  uniform float iTime;
  uniform vec2 iResolution;
  uniform sampler2D iFluid;
  uniform float uDistortionAmount;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  uniform vec3 uColor3;
  uniform vec3 uColor4;
  uniform float uColorIntensity;
  uniform float uSoftness;
  uniform float uIdleSpeed;

  varying vec2 vUv;

  void main() {
    vec2 fragCoord = vUv * iResolution;
    vec4 fluid = texture2D(iFluid, vUv);
    vec2 fluidVel = fluid.xy;

    float mr = min(iResolution.x, iResolution.y);
    vec2 uv = (fragCoord * 2.0 - iResolution.xy) / mr;

    uv += fluidVel * (0.5 * uDistortionAmount);
    float t = iTime * uIdleSpeed;
    float d = -t * 0.5;
    float a = 0.0;
    for (float i = 0.0; i < 8.0; ++i) {
      a += cos(i - d - a * uv.x);
      d += sin(uv.y * i + a);
    }
    d += t * 0.5;

    float mixer1 = mix(cos(uv.x * d) * 0.5 + 0.5, 0.5, clamp(uSoftness * 0.1, 0.0, 0.9));
    float mixer2 = mix(cos(uv.y * a) * 0.5 + 0.5, 0.5, clamp(uSoftness * 0.1, 0.0, 0.9));
    float mixer3 = mix(sin(d + a) * 0.5 + 0.5, 0.5, clamp(uSoftness * 0.1, 0.0, 0.9));

    vec3 col = mix(uColor1, uColor2, mixer1);
    col = mix(col, uColor3, mixer2);
    col = mix(col, uColor4, mixer3 * 0.4);

    gl_FragColor = vec4(col * uColorIntensity, 1.0);
  }
`;

function toLinearVector3(color: string): THREE.Vector3 {
  const c = new THREE.Color(color);
  c.convertSRGBToLinear();
  return new THREE.Vector3(c.r, c.g, c.b);
}

export default function UploadFluidBackground({
  className,
  color1 = "#030916",
  color2 = "#0b2f75",
  color3 = "#1a73d9",
  color4 = "#6fe6ff",
}: UploadFluidBackgroundProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    container.appendChild(renderer.domElement);

    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    const rtOptions: THREE.WebGLRenderTargetOptions = {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      type: THREE.HalfFloatType,
      depthBuffer: false,
      stencilBuffer: false,
    };

    const fluidTargetA = new THREE.WebGLRenderTarget(1, 1, rtOptions);
    const fluidTargetB = fluidTargetA.clone();
    let currentFluidTarget = fluidTargetA;
    let previousFluidTarget = fluidTargetB;

    const fluidMaterial = new THREE.ShaderMaterial({
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(1, 1) },
        iMouse: { value: new THREE.Vector4(0, 0, 0, 0) },
        iFrame: { value: 0 },
        iPreviousFrame: { value: null },
        uBrushSize: { value: 24.0 },
        uBrushStrength: { value: 0.5 },
        uFluidDecay: { value: 0.98 },
        uTrailLength: { value: 0.84 },
        uStopDecay: { value: 0.86 },
        uFlowSpeed: { value: 0.12 },
      },
      vertexShader,
      fragmentShader: fluidShader,
    });

    const displayMaterial = new THREE.ShaderMaterial({
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new THREE.Vector2(1, 1) },
        iFluid: { value: null },
        uDistortionAmount: { value: 0.5 },
        uColor1: { value: toLinearVector3(color1) },
        uColor2: { value: toLinearVector3(color2) },
        uColor3: { value: toLinearVector3(color3) },
        uColor4: { value: toLinearVector3(color4) },
        uColorIntensity: { value: 0.78 },
        uSoftness: { value: 1.0 },
        uIdleSpeed: { value: 0.9 },
      },
      vertexShader,
      fragmentShader: displayShader,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const fluidPlane = new THREE.Mesh(geometry, fluidMaterial);
    const displayPlane = new THREE.Mesh(geometry, displayMaterial);
    const fluidScene = new THREE.Scene();
    const displayScene = new THREE.Scene();
    fluidScene.add(fluidPlane);
    displayScene.add(displayPlane);

    let mouseX = 0;
    let mouseY = 0;
    let lastMoveTime = 0;
    let frameCount = 0;
    let disposed = false;
    let rafId = 0;

    const setRendererSize = () => {
      const rect = container.getBoundingClientRect();
      const width = Math.max(1, Math.floor(rect.width));
      const height = Math.max(1, Math.floor(rect.height));
      renderer.setSize(width, height, false);
      fluidMaterial.uniforms.iResolution.value.set(width, height);
      displayMaterial.uniforms.iResolution.value.set(width, height);
      fluidTargetA.setSize(width, height);
      fluidTargetB.setSize(width, height);
    };

    const resizeObserver = new ResizeObserver(() => {
      setRendererSize();
    });
    resizeObserver.observe(container);
    setRendererSize();

    const onPointerMove = (event: PointerEvent) => {
      const rect = container.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = rect.height - (event.clientY - rect.top);
      fluidMaterial.uniforms.iMouse.value.set(x, y, mouseX, mouseY);
      mouseX = x;
      mouseY = y;
      lastMoveTime = performance.now();
    };

    container.addEventListener("pointermove", onPointerMove);

    const animate = (timeMs: number) => {
      if (disposed) {
        return;
      }
      rafId = window.requestAnimationFrame(animate);
      const time = timeMs * 0.001;

      fluidMaterial.uniforms.iTime.value = time;
      displayMaterial.uniforms.iTime.value = time;
      fluidMaterial.uniforms.iFrame.value = frameCount;

      if (performance.now() - lastMoveTime > 120) {
        fluidMaterial.uniforms.iMouse.value.set(0, 0, 0, 0);
      }

      fluidMaterial.uniforms.iPreviousFrame.value = previousFluidTarget.texture;
      renderer.setRenderTarget(currentFluidTarget);
      renderer.render(fluidScene, camera);

      displayMaterial.uniforms.iFluid.value = currentFluidTarget.texture;
      renderer.setRenderTarget(null);
      renderer.render(displayScene, camera);

      const nextCurrent = previousFluidTarget;
      previousFluidTarget = currentFluidTarget;
      currentFluidTarget = nextCurrent;
      frameCount += 1;
    };

    rafId = window.requestAnimationFrame(animate);

    return () => {
      disposed = true;
      window.cancelAnimationFrame(rafId);
      container.removeEventListener("pointermove", onPointerMove);
      resizeObserver.disconnect();

      fluidTargetA.dispose();
      fluidTargetB.dispose();
      fluidMaterial.dispose();
      displayMaterial.dispose();
      geometry.dispose();
      renderer.dispose();

      if (renderer.domElement.parentElement === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [color1, color2, color3, color4]);

  return <div ref={containerRef} className={className} aria-hidden="true" />;
}
